import { DialogContent } from "components/Dialog";
import "./index.css";
import { useCallback, useEffect, useState } from "react";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import {
  ACHResponse,
  CreditCardResponse,
  PaymentPlanFrequency,
  PaymentPlanResponse,
  StudentLimitedResponse,
  PaymentsService,
  StudentsService,
  PaymentMethodType,
} from "client/openapi";
import PaymentMethods from "../../../../../../../../components/PaymentMethods/PaymentMethods";
import { APIResponse, PageStatus } from "types";
import Notifications from "util/notifications";
import CardOption from "../../../../../../../../components/PaymentMethods/cards";
import BankOption from "../../../../../../../../components/PaymentMethods/bank";

export default function AutomaticPaymentsModal({
  student,
  orgId,
  paymentPlan,
  setPaymentPlan,
}: {
  student: StudentLimitedResponse;
  orgId: number;
  paymentPlan?: PaymentPlanResponse;
  setPaymentPlan: (paymentPlan: PaymentPlanResponse) => void;
}) {
  const [isPaymentPlan, setIsPaymentPlan] = useState<boolean>(false);

  const [editing, setEditing] = useState<boolean>(false);
  const [checked, setChecked] = useState([false, true]);
  const [interval, setInterval] = useState<number>();

  const [newInterval, setNewInterval] = useState<number>();
  const [invalidInterval, setInvalidInterval] = useState<boolean>(false);

  type AllPaymentMethods = CreditCardResponse | ACHResponse;
  const [paymentMethodsOnFile, setPaymentMethodsOnFile] = useState<
    AllPaymentMethods[]
  >([]);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<
    CreditCardResponse | ACHResponse | string
  >();
  const [currentPaymentMethodType, setCurrentPaymentMethodType] =
    useState<PaymentMethodType>();
  const [newPaymentMethod, setNewPaymentMethod] = useState<
    CreditCardResponse | ACHResponse | string
  >();
  const [newPaymentMethodType, setNewPaymentMethodType] =
    useState<PaymentMethodType>();

  const [status, setStatus] = useState<PageStatus>(PageStatus.EDITING);
  const [error, setError] = useState<APIResponse>();

  const [daysInterval, setDaysInterval] = useState<string>("");

  const [preBillingEnabled, setPreBillingEnabled] = useState<boolean>(false);
  const [preBillingWeeks, setPreBillingWeeks] = useState<string>("2");

  const getAndSetPaymentMethodsOnFile = useCallback(async () => {
    let newMethods: AllPaymentMethods[] = [];

    try {
      const existing_cards = await PaymentsService.getStudentCards({
        studentId: student.id,
      });
      if (existing_cards) {
        newMethods = [...newMethods, ...existing_cards];
      }
    } catch (error: any) {
      setError(error);
      setStatus(PageStatus.ERROR);
      console.error("Error:", error);
    }

    try {
      const existing_banks = await PaymentsService.getStudentAchAccounts({
        studentId: student.id,
      });
      if (existing_banks) {
        newMethods = [...newMethods, ...existing_banks];
      }
    } catch (error: any) {
      setError(error);
      setStatus(PageStatus.ERROR);
      console.error("Error:", error);
    }

    setPaymentMethodsOnFile(newMethods);
  }, [orgId, student.id]);

  useEffect(() => {
    getAndSetPaymentMethodsOnFile();
  }, [getAndSetPaymentMethodsOnFile]);

  const findPaymentMethodById = useCallback(
    (paymentMethodId) => {
      return Array.from(paymentMethodsOnFile).find(
        (paymentMethod) => paymentMethod.payment_method_id === paymentMethodId
      );
    },
    [paymentMethodsOnFile]
  );

  useEffect(() => {
    if (student) {
      if (paymentPlan && paymentPlan.frequency === PaymentPlanFrequency.DAILY) {
        setChecked([true, false]);
        setInterval(paymentPlan.interval);
        setCurrentPaymentMethodType(
          paymentPlan?.payment_method_type as PaymentMethodType
        );

        if (
          paymentPlan.payment_method_type === PaymentMethodType.CARD ||
          paymentPlan.payment_method_type === PaymentMethodType.ACH
        ) {
          const method = findPaymentMethodById(paymentPlan.payment_method_id);
          setCurrentPaymentMethod(method);
          setNewPaymentMethod(method);
        } else if (
          paymentPlan.payment_method_type === PaymentMethodType.INVOICE
        ) {
          setCurrentPaymentMethod(paymentPlan.invoice_email as string);
          setNewPaymentMethod(paymentPlan.invoice_email as string);
        } else {
          setCurrentPaymentMethod(undefined);
        }
        setNewPaymentMethodType(
          paymentPlan?.payment_method_type as PaymentMethodType
        );

        setIsPaymentPlan(true);
        setEditing(false);

        // Pre-fill daysInterval if interval exists
        if (paymentPlan.interval) {
          setDaysInterval(String(paymentPlan.interval));
          setNewInterval(paymentPlan.interval);
          setInvalidInterval(false);
        }

        setPreBillingEnabled(paymentPlan.pre_billing_enabled ?? false);
        setPreBillingWeeks(
          String(paymentPlan.pre_billing_lookahead_weeks ?? 2)
        );
      } else {
        setChecked([false, true]);
        setIsPaymentPlan(false);
      }
    }
  }, [findPaymentMethodById, paymentMethodsOnFile, paymentPlan, student]);

  const updatePaymentPlan = async () => {
    if (
      checked[0] === true &&
      (newPaymentMethod === "" || newPaymentMethod === null)
    ) {
      setError({
        message: "Invalid Payment Method",
      });
      return;
    }

    if (checked[0] === true && (invalidInterval || !daysInterval)) {
      setError({
        message: "Please enter a valid number of days (<= 90)",
      });
      return;
    }

    // Handle the autopay = YES scenario
    if (checked[0] === true) {
      StudentsService.updatePaymentPlan({
        studentId: student.id,
        requestBody: {
          payment_method_id:
            newPaymentMethodType === PaymentMethodType.CARD ||
            newPaymentMethodType === PaymentMethodType.ACH
              ? (newPaymentMethod as CreditCardResponse | ACHResponse)
                  .payment_method_id
              : undefined,
          invoice_email:
            newPaymentMethodType === PaymentMethodType.INVOICE
              ? (newPaymentMethod as string)
              : undefined,
          payment_method_type: newPaymentMethodType,
          frequency:
            checked[0] === true
              ? PaymentPlanFrequency.DAILY
              : PaymentPlanFrequency.NONE,
          interval: checked[0] === true ? newInterval : 1,
          pre_billing_enabled: preBillingEnabled,
          pre_billing_lookahead_weeks: preBillingEnabled
            ? Number(preBillingWeeks)
            : undefined,
        },
      })
        .then((plan) => {
          setStatus(PageStatus.SUCCESS);
          setPaymentPlan(plan);
          setEditing(false);
          Notifications.success(`Student payment plan updated!`);
        })
        .catch((err) => {
          setError(err);
          setStatus(PageStatus.ERROR);
        });
    }

    // Handle the autopay = NO scenario
    if (checked[1] === true) {
      StudentsService.updatePaymentPlan({
        studentId: student.id,
        requestBody: {
          frequency: PaymentPlanFrequency.NONE,
          payment_method_type: null,
          payment_method_id: null,
          invoice_email: null,
          interval: 1,
          pre_billing_enabled: false,
        },
      })
        .then((plan) => {
          setStatus(PageStatus.SUCCESS);
          setPaymentPlan(plan);
          setEditing(false);
          Notifications.success(`Student payment plan updated!`);
        })
        .catch((err) => {
          setError(err);
          setStatus(PageStatus.ERROR);
        });
    } else {
      setStatus(PageStatus.ERROR);
    }
  };

  const handleDaysIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Remove all non-digit characters (this removes decimals)
    value = value.replace(/\D/g, "");

    if (value === "") {
      // Empty allowed, invalid interval and no error
      setDaysInterval("");
      setNewInterval(undefined);
      setInvalidInterval(true); // Can't save when empty
      // Clear any correction errors if they exist
      if (error?.message?.includes("Corrected")) {
        setError(undefined);
      }
      return;
    }

    let num = Number(value);

    if (num < 1) {
      // Correct to 1
      num = 1;
      setError({ message: "Days must be greater than zero. Corrected to 1." });
      setInvalidInterval(false);
    } else if (num > 90) {
      // Correct to 90
      num = 90;
      setError({
        message:
          "Please bundle no more than three months of charges (max 90 days).",
      });
      setInvalidInterval(false);
    } else {
      // Valid value
      // Clear any correction errors if exist
      if (error?.message?.includes("Corrected")) {
        setError(undefined);
      }
      setInvalidInterval(false);
    }

    setDaysInterval(String(num));
    setNewInterval(num);
  };

  const handleWeeksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    value = value.replace(/\D/g, "");

    if (value === "") {
      setPreBillingWeeks("");
      return;
    }

    let num = Number(value);

    if (num < 1) {
      num = 1;
      setError({ message: "Weeks must be at least 1. Corrected to 1." });
    } else if (num > 12) {
      num = 12;
      setError({
        message: "Lookahead cannot exceed 12 weeks. Corrected to 12.",
      });
    }

    setPreBillingWeeks(String(num));
  };

  // Prevent typing decimals or scientific notation keys
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "." || e.key === "e" || e.key === "E") {
      e.preventDefault();
    }
  };

  function clearPaymentPlan() {
    setNewInterval(undefined);
    setInvalidInterval(true);
    setNewPaymentMethod("");
    setDaysInterval("");
    setPreBillingEnabled(false);
    setPreBillingWeeks("2");
    // Clear errors if any
    if (error?.message?.includes("Corrected")) {
      setError(undefined);
    }
  }

  useEffect(() => {
    if (error) {
      Notifications.error(error?.message || "An unexpected error occurred.");
    }
  }, [error]);

  const showFields = checked[0] && (!isPaymentPlan || editing);

  const saveDisabled =
    showFields &&
    (!newPaymentMethod ||
      newPaymentMethod === "" ||
      invalidInterval ||
      !daysInterval ||
      (preBillingEnabled && !preBillingWeeks));

  return (
    <DialogContent className="dialog-content max-w-[800px]">
      <div className="mt-3 font-semibold text-center header text-lg">
        Automatic Billing
      </div>

      <div className="mt-3 border border-gray-300 border-2 rounded-lg p-4">
        <p className="header font-medium text-sm">
          <div className="mb-2 font-bold">Auto bill student for meetings:</div>
          <input
            type="radio"
            className={`!border-black ml-2 rounded !bg-none ${
              checked[0] && "!bg-green-400"
            } focus:!outline-none`}
            checked={checked[0]}
            onChange={() => setChecked([true, false])}
          />{" "}
          &nbsp;Yes
          <input
            type="radio"
            className={`!border-black ml-2 rounded !bg-none ${
              checked[1] && "!bg-green-400"
            } focus:!outline-none`}
            checked={checked[1]}
            onChange={() => {
              setChecked([false, true]);
              clearPaymentPlan();
            }}
          />{" "}
          &nbsp;No
        </p>

        {checked[0] && isPaymentPlan && !editing && (
          <div>
            <div className="header text-sm mt-3">
              <div>
                <div className="font-bold header mb-2 text-sm">
                  Payment Plan:
                </div>
                Student will be charged every {interval} day(s) using:
                <div className="mt-2 mb-2">
                  {currentPaymentMethod ? (
                    <div>
                      {currentPaymentMethodType === PaymentMethodType.CARD && (
                        <CardOption
                          key={
                            (currentPaymentMethod as CreditCardResponse)
                              .payment_method_id
                          }
                          card={currentPaymentMethod as CreditCardResponse}
                        />
                      )}
                      {currentPaymentMethodType === PaymentMethodType.ACH && (
                        <BankOption
                          key={
                            (currentPaymentMethod as ACHResponse)
                              .payment_method_id
                          }
                          bank={currentPaymentMethod as ACHResponse}
                        />
                      )}
                      {currentPaymentMethodType ===
                        PaymentMethodType.INVOICE && (
                        <span className="text-sm font-bold">
                          {currentPaymentMethod as string}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="font-semibold text-sm">
                      No payment method has been set. Add one:
                    </span>
                  )}
                </div>
                {paymentPlan?.pre_billing_enabled && (
                  <div className="mt-2 text-sm">
                    Pre-billing: <span className="font-semibold">Enabled</span>{" "}
                    ({paymentPlan.pre_billing_lookahead_weeks} week
                    {paymentPlan.pre_billing_lookahead_weeks !== 1
                      ? "s"
                      : ""}{" "}
                    ahead)
                  </div>
                )}
                <div className="text-xs mt-2">
                  Note: For any given subject, auto-pay will use all credits a
                  student has on file before billing with their payment method.
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Button
                color={ButtonColor.PURPLE}
                size={ButtonSize.SMALL}
                onClick={() => setEditing(!editing)}
              >
                Edit Payment Plan
              </Button>
            </div>
          </div>
        )}

        {showFields && (
          <>
            <p className="mt-4 mb-2 text-sm font-bold">Bill every:</p>
            <input
              type="number"
              className="block w-11/12 h-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              id="days"
              name="days"
              placeholder="14 days"
              step={1}
              value={daysInterval}
              onKeyDown={handleKeyDown}
              onChange={handleDaysIntervalChange}
            />
            <p className="mt-4 mb-2 text-sm font-bold">Bill to:</p>
            <div className="px-2">
              <PaymentMethods
                selectable
                studentIfInitiatedByAdmin={student}
                setPaymentMethod={setNewPaymentMethod}
                setPaymentMethodType={setNewPaymentMethodType}
                defaultSelectedId={
                  paymentPlan?.payment_method_id ??
                  paymentPlan?.invoice_email ??
                  undefined
                }
              />
            </div>
            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                <input
                  type="checkbox"
                  className="!border-black rounded !bg-none focus:!outline-none"
                  checked={preBillingEnabled}
                  onChange={(e) => setPreBillingEnabled(e.target.checked)}
                />
                Enable Pre-Billing
              </label>
              {preBillingEnabled && (
                <>
                  <p className="mt-4 mb-2 text-sm font-bold">
                    Lookahead Weeks:
                  </p>
                  <input
                    type="number"
                    className="block w-11/12 h-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    id="weeks"
                    name="weeks"
                    placeholder="2 weeks"
                    step={1}
                    value={preBillingWeeks}
                    onKeyDown={handleKeyDown}
                    onChange={handleWeeksChange}
                  />
                </>
              )}
            </div>
            {isPaymentPlan && (
              <div className="mt-4 ml-1">
                <Button
                  color={ButtonColor.PURPLE}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    setEditing(false);
                    // Restore existing plan values instead of clearing
                    if (paymentPlan) {
                      setDaysInterval(String(paymentPlan.interval));
                      setNewInterval(paymentPlan.interval);
                      setInvalidInterval(false);
                      setPreBillingEnabled(
                        paymentPlan.pre_billing_enabled ?? false
                      );
                      setPreBillingWeeks(
                        String(paymentPlan.pre_billing_lookahead_weeks ?? 2)
                      );
                    }
                    // Restore payment method from existing plan
                    if (paymentPlan) {
                      if (
                        paymentPlan.payment_method_type ===
                          PaymentMethodType.CARD ||
                        paymentPlan.payment_method_type ===
                          PaymentMethodType.ACH
                      ) {
                        setNewPaymentMethod(
                          findPaymentMethodById(paymentPlan.payment_method_id)
                        );
                      } else if (
                        paymentPlan.payment_method_type ===
                        PaymentMethodType.INVOICE
                      ) {
                        setNewPaymentMethod(
                          paymentPlan.invoice_email as string
                        );
                      }
                      setNewPaymentMethodType(
                        paymentPlan.payment_method_type as PaymentMethodType
                      );
                    }
                    if (error?.message?.includes("Corrected")) {
                      setError(undefined);
                    }
                  }}
                >
                  Cancel Editing
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="w-full text-center mt-6">
        <Button
          color={saveDisabled ? ButtonColor.GRAY : ButtonColor.GREEN}
          size={ButtonSize.SMALL}
          onClick={updatePaymentPlan}
          disabled={saveDisabled}
        >
          Save
        </Button>
      </div>
    </DialogContent>
  );
}
